import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import PublicLayout from '../../components/layout/PublicLayout';
import Button from '../../components/common/Button';
import Pipeline from '../../components/common/Pipeline';
import { orderAPI } from '../../services/api';
import { todayIsoDate } from '../../utils/dateTime';

const TrackOrder = () => {
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const onSubmit = async (data) => {
    if (!data.orderId) {
      toast.error('Order ID is required');
      return;
    }
    
    setLoading(true);
    
    try {
      // Call the real API to get order by ID
      const normalizedOrderId = String(data.orderId || '').trim().replace(/\s+/g, '');
      const response = await orderAPI.getOrder(normalizedOrderId);
      
      if (response.data && response.data.success) {
        const order = response.data.data;
        
        // Map the API response to the UI format
        const mappedOrderData = {
          id: order.id, // Store database ID for refill navigation
          orderId: order.orderId || normalizedOrderId,
          status: mapOrderStatus(order.orderStatus),
          currentStage: mapCurrentStage(order.orderStatus),
          stages: generateOrderStages(order.orderStatus),
          reason: order.reason, // Store refill reason
          details: {
            product: order.productName || 'N/A',
            amount: order.orderAmount ? `₹${order.orderAmount}` : 'N/A',
            orderDate: order.orderDate || todayIsoDate(),
            estimatedDelivery: order.estimatedDeliveryDate || 'N/A',
            trackingNumber: order.trackingId || 'N/A',
            customerName: order.reviewerName || 'N/A',
            email: order.userEmail || 'N/A',
            whatsappNumber: order.whatsappNumber || 'N/A',
            mediatorName: order.mediatorName || 'N/A',
            orderStatus: order.orderStatus || 'N/A'
          }
        };
        
        setOrderData(mappedOrderData);
        toast.success('Order found successfully!');
      } else {
        toast.error(response.data?.message || 'Order not found');
      }
    } catch (error) {
      console.error('Error tracking order:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to track order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to map order status to UI status
  const mapOrderStatus = (orderStatus) => {
    const statusMap = {
      'ORDER_FORM_FILLED': 'processing',
      'APPROVED': 'processing',
      'PAID': 'shipped',
      'REJECTED': 'cancelled',
      'CANCELLED': 'cancelled',
      'DELIVERED': 'delivered',
      'ORDER_FORM_REFILL_REQUEST': 'processing',
      'REFUND_FORM_REFILL_REQUEST': 'processing',
      'REJECT_AND_REFILL': 'processing'
    };
    return statusMap[orderStatus] || 'processing';
  };

  // Helper function to map current stage based on status
  const mapCurrentStage = (orderStatus) => {
    const stageMap = {
      'ORDER_FORM_FILLED': 1,
      'APPROVED': 2,
      'PAID': 3,
      'DELIVERED': 4,
      'REJECTED': 0,
      'CANCELLED': 0
    };
    return stageMap[orderStatus] || 1;
  };

  // Generate order stages based on current status
  const generateOrderStages = (orderStatus) => {
    const currentStage = mapCurrentStage(orderStatus);
    const today = todayIsoDate();
    
    return [
      { name: 'Order Placed', completed: currentStage >= 1, date: currentStage >= 1 ? today : null },
      { name: 'Order Confirmed', completed: currentStage >= 2, date: currentStage >= 2 ? today : null },
      { name: 'Processing', completed: currentStage >= 3, date: currentStage >= 3 ? today : null },
      { name: 'Shipped', completed: currentStage >= 4, date: currentStage >= 4 ? today : null },
      { name: 'Delivered', completed: orderStatus === 'DELIVERED', date: orderStatus === 'DELIVERED' ? today : null }
    ];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'var(--primary)';
      case 'shipped': return 'var(--accent)';
      case 'processing': return 'var(--orange)';
      case 'cancelled': return 'var(--red)';
      default: return 'var(--text-muted)';
    }
  };

  return (
    <PublicLayout title="Track Order" showBack>
      <div style={{ maxWidth: '896px', margin: '0 auto' }}>
        {!orderData ? (
          /* Search Form */
          <div className="card">
            {/* Header */}
            <div 
              style={{
                padding: '24px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, var(--teal), #0D9488)',
              }}
            >
              <h2 
                className="outfit"
                style={{ 
                  fontWeight: '700', 
                  fontSize: '24px', 
                  marginBottom: '8px',
                  color: 'white' 
                }}
              >
                Track Your Order
              </h2>
              <p 
                style={{ 
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.9)' 
                }}
              >
                Enter your Order ID to track your order status
              </p>
            </div>

            {/* Form Content */}
            <div style={{ padding: '24px' }}>
              <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label className="label">Order ID *</label>
                  <input
                    {...register('orderId', { 
                      required: 'Order ID is required',
                      pattern: {
                        value: /^[A-Z0-9-]{10,30}$/i,
                        message: 'Invalid Order ID format'
                      }
                    })}
                    type="text"
                    placeholder="Enter your Order ID (e.g., 403-4455221-4234216)"
                    style={{
                      borderColor: errors.orderId ? 'var(--red)' : 'var(--border)'
                    }}
                  />
                  {errors.orderId && (
                    <p className="text-red-500">{errors.orderId.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  loading={loading}
                  style={{ width: '100%' }}
                >
                  Track Order
                </Button>
              </form>

              {/* Quick Actions */}
              <div style={{ marginTop: '32px', textAlign: 'center' }}>
                <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                  Need help with your order?
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate('/refund')}
                  >
                    Request Refund
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate('/login?role=buyer')}
                  >
                    Contact Support
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Order Details */
          <div className="card">
            {/* Header */}
            <div style={{ padding: '24px', borderBottom: `1px solid var(--border)` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 
                  className="outfit"
                  style={{ 
                    fontWeight: '700', 
                    fontSize: '20px',
                    color: 'var(--text)' 
                  }}
                >
                  Order Details
                </h2>
                <span 
                  style={{
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: `${getStatusColor(orderData.status)}22`,
                    color: getStatusColor(orderData.status)
                  }}
                >
                  {orderData.status.toUpperCase()}
                </span>
              </div>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                Order ID: <span style={{ fontWeight: '600', color: 'var(--text)' }}>{orderData.orderId}</span>
              </p>
            </div>

            {/* Order Info */}
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Product</p>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                    {orderData.details.product}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Amount</p>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--primary)' }}>
                    {orderData.details.amount}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Order Date</p>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                    {orderData.details.orderDate}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Est. Delivery</p>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                    {orderData.details.estimatedDelivery}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Customer Name</p>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                    {orderData.details.customerName}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Email</p>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                    {orderData.details.email}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>WhatsApp Number</p>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                    {orderData.details.whatsappNumber}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Order Status</p>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: getStatusColor(orderData.status) }}>
                    {orderData.details.orderStatus}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Mediator</p>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                    {orderData.details.mediatorName}
                  </p>
                </div>
              </div>

              {/* Tracking Number */}
              <div style={{ 
                padding: '12px', 
                background: 'var(--bg-deep)', 
                borderRadius: '8px', 
                marginBottom: '24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Tracking Number</p>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                    {orderData.details.trackingNumber}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(orderData.details.trackingNumber);
                    // You could add a toast notification here
                  }}
                >
                  Copy
                </Button>
              </div>

              {/* Reason for Refill */}
              {(orderData.details.orderStatus === 'ORDER_FORM_REFILL_REQUEST'
                || orderData.details.orderStatus === 'REFUND_FORM_REFILL_REQUEST'
                || orderData.details.orderStatus === 'REJECT_AND_REFILL') && orderData.reason && (
                <div style={{ padding: '16px', background: '#fef2f2', borderRadius: '12px', marginBottom: '24px', border: '1px solid #fee2e2' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#b91c1c' }}>
                    Refill Reason
                  </h4>
                  <p style={{ fontSize: '13px', color: '#b91c1c', margin: 0 }}>{orderData.reason}</p>
                </div>
              )}

              {/* Order Pipeline */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', marginBottom: '16px' }}>
                  Order Status
                </h3>
                <Pipeline
                  stages={orderData.stages}
                  currentStage={orderData.currentStage}
                />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setOrderData(null);
                    reset();
                  }}
                >
                  Track Another Order
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate('/refund')}
                >
                  Request Refund
                </Button>
                {orderData.details.orderStatus === 'ORDER_FORM_REFILL_REQUEST' && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/order?edit=${orderData.id}&refill=true`)}
                  >
                    Refill Order Form
                  </Button>
                )}
                {(orderData.details.orderStatus === 'REFUND_FORM_REFILL_REQUEST'
                  || orderData.details.orderStatus === 'REJECT_AND_REFILL') && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/refund?edit=${orderData.id}&refill=true&orderId=${orderData.details.trackingNumber || orderData.details.orderId}`)}
                  >
                    Refill Refund Form
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
};

export default TrackOrder;
